import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera, CheckCircle2, ChevronLeft, ClipboardCheck, CloudOff, Coffee, Loader2, LogOut, MapPin,
  Package, Phone, Play, RefreshCw, Truck, Upload, AlertTriangle, Clock, X
} from "lucide-react";
import API from "../services/api";
import { enqueue, flush, count as queuedCount } from "../services/uploadQueue";
import { getPosition, isNative, onNetworkChange, takePhoto } from "../services/native";
import {
  cachedShift, clockIn, clockOut, deviceDate, dismissFailed, endBreak, failedEvents, flushClock,
  getCurrent, nzClock, parseServerInstant, pendingCount, rememberCheck, startBreak, submitCheck,
} from "../services/timeclock";
import PreCheck, { lastTruckId, plateFor, warmPreCheckCache } from "./PreCheck";

/* Who has to check the truck before the clock starts. Offsiders do not drive,
 * so they are never asked which truck they took - the operator's rule, and a
 * picker with no right answer. Read the same way permissions.js does: a
 * storage read can throw in a locked-down profile, and that must not take
 * the shift bar with it. */
function crewRole() {
  try { return localStorage.getItem("dav_role") || ""; } catch { return ""; }
}

/** "4h 07m" since the shift started. */
function elapsedLabel(startedAt, now) {
  const start = parseServerInstant(startedAt);
  if (!start) return "";
  const mins = Math.max(0, Math.floor((now - start.getTime()) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

/** "12m" since a break started. The instant is the server's naive UTC or the
 *  device ISO of a punch still on the phone - parseServerInstant reads both. */
function breakElapsed(startedAt, now) {
  const start = parseServerInstant(startedAt);
  if (!start) return "";
  return `${Math.max(0, Math.floor((now - start.getTime()) / 60000))}m`;
}

/* A break's wall-clock times as the office formatted them ("12:00"); a break
 * the phone queued itself was formatted with nzClock when it was punched. No
 * timezone maths here - a naive server string must never go through Date. */
function breakStart(b) { return b.start || (b.started_at ? nzClock(b.started_at) : ""); }
function breakFinish(b) { return b.finish || (b.finished_at ? nzClock(b.finished_at) : ""); }
function breakSpan(b) { return `${breakStart(b)}–${breakFinish(b)}`; }
function isOpenBreak(b) { return !!b && (b.open || !b.finished_at); }

/** The break still running on a shift, if any - the LAST one with no finish,
 *  which is the one an End break would close. */
function openBreakOf(shift) {
  const list = shift?.breaks || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (isOpenBreak(list[i])) return list[i];
  }
  return null;
}

/** How a punch (or a pre-check) the office refused is named back to the driver. */
function punchLabel(event) {
  if (event.kind === "in") return "Start of shift";
  if (event.kind === "out") return "End of shift";
  if (event.kind === "break_start") return "Break start";
  if (event.kind === "break_end") return "Break end";
  if (event.kind === "check") return `Pre-check${event.truck_plate ? ` on ${event.truck_plate}` : ""}`;
  return `${event.minutes || 0} min break`;      // legacy minutes punch from an older app
}

/* Photos come off a phone camera at 3-12MB. Uploading that over a rural
 * connection is slow enough that drivers give up, and the server disk was
 * already 86% full - so every image is resized and re-encoded before it
 * leaves the device. ~1600px on the long edge stays legible as proof of
 * delivery while landing around 200-400KB. */
async function compressImage(file, maxEdge = 1600, quality = 0.72) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    bitmap.close?.();
    // If compression somehow made it bigger, keep the original.
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;                    // HEIC on older browsers, etc.
  }
}

export default function DriverApp({ onExit, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [selected, setSelected] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  /* Seeded from localStorage so the shift bar is right on a cold open with no
   * signal - sw.js never caches API responses, so there is nothing else to
   * read until /time/current answers. */
  const [shiftState, setShiftState] = useState(() => cachedShift());
  const [clockPending, setClockPending] = useState(0);
  const [clockFailed, setClockFailed] = useState([]);
  const [clockBusy, setClockBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  /* The truck pre-check screen. { mode: "start" | "check", truckId } while it
   * is open. "start" is the gate in front of Start shift; "check" is the bar
   * button - today's sheet read-only, or a form for a driver not yet on
   * shift. Which truck was checked lives with the shift state (see
   * timeclock.js) so the fault card and the plate on the bar survive a
   * restart with no signal. */
  const [precheck, setPrecheck] = useState(null);
  // The CREW record's role (what the office says this person is) wins over
  // the login's cached role: the two drifted for one offsider whose login
  // predated the offsider role, and he was being asked to pre-check a truck.
  const role = shiftState?.role || crewRole();

  // "My hours" - the driver's own start and finish times for the fortnight.
  // Self-scoped on the server (/time/my-shifts only ever answers for the
  // signed-in person), so there is nothing to filter here. The last good
  // answer is cached so the list still opens in a dead zone.
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursOffset, setHoursOffset] = useState(0);      // 0 = this fortnight, -1 = last
  const [hoursData, setHoursData] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dav_my_hours") || "null"); } catch { return null; }
  });
  const [hoursBusy, setHoursBusy] = useState(false);
  const [hoursErr, setHoursErr] = useState("");

  const shiftDate = (ymd, days) => {
    // built from parts, never parsed from an ISO string - a bare "YYYY-MM-DD"
    // through new Date() is read as UTC and lands on the wrong NZ day
    const [y, m, d] = String(ymd).split("-").map(Number);
    const dt = new Date(y, m - 1, d + days);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };
  const prettyDay = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = String(ymd).split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "short" });
  };

  const loadHours = useCallback(async (offset) => {
    setHoursBusy(true);
    setHoursErr("");
    try {
      let fn = shiftState?.fortnight;
      if (!fn?.start || !fn?.end) {
        fn = (await API.get("/time/current")).data?.fortnight;
      }
      const from = shiftDate(fn.start, 14 * offset);
      const to = shiftDate(fn.end, 14 * offset);
      const res = await API.get(`/time/my-shifts?from=${from}&to=${to}`);
      const data = { ...res.data, offset, status: offset === 0 ? fn.status : undefined, fetched_at: Date.now() };
      setHoursData(data);
      try { localStorage.setItem("dav_my_hours", JSON.stringify(data)); } catch { /* storage may be off */ }
    } catch (err) {
      setHoursErr(err?.response?.data?.detail || "Could not load your hours - needs signal");
    } finally {
      setHoursBusy(false);
    }
  }, [shiftState]);

  const openHours = (offset = 0) => {
    setHoursOffset(offset);
    setHoursOpen(true);
    loadHours(offset);
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await API.get("/driver/my-run");
      setRun(res.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        setError(err?.response?.data?.detail || "Could not load your run. Pull to retry.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshQueue = useCallback(async () => setPending(await queuedCount()), []);

  const drain = useCallback(async () => {
    const sent = await flush(API);
    await refreshQueue();
    if (sent > 0) {
      setToast(`${sent} queued photo${sent > 1 ? "s" : ""} uploaded`);
      load();
    }
  }, [load, refreshQueue]);

  const refreshClockQueue = useCallback(async () => {
    setClockPending(await pendingCount());
    setClockFailed(await failedEvents());
  }, []);

  const loadShift = useCallback(async () => {
    setShiftState(await getCurrent(API));
  }, []);

  /* Punches only ever leave the phone while the app is open - there is no
   * Background Sync - so try on every mount and every time signal returns. */
  const drainClock = useCallback(async () => {
    const { sent } = await flushClock(API);
    await refreshClockQueue();
    if (sent > 0) setToast(`${sent} held ${sent > 1 ? "entries" : "entry"} sent to the office`);
    loadShift();
  }, [loadShift, refreshClockQueue]);

  /* The fleet list and the checklist template are cached on every online
   * open so the pre-check form opens in a yard with no signal. Never throws. */
  const warmChecks = useCallback(() => {
    if (navigator.onLine) warmPreCheckCache(API).catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    refreshQueue();
    refreshClockQueue();
    loadShift();
    const unsubscribe = onNetworkChange((connected) => {
      setOnline(connected);
      if (connected) { drain(); drainClock(); warmChecks(); }
    });
    if (navigator.onLine) { drain(); drainClock(); warmChecks(); }
    return unsubscribe;
  }, [load, drain, refreshQueue, loadShift, drainClock, refreshClockQueue, warmChecks]);

  /* Ticks the elapsed clock. 20s keeps the minute honest without waking the
   * phone every second for a display that only changes once a minute. */
  useEffect(() => {
    if (!shiftState?.open_shift) return undefined;
    const t = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(t);
  }, [shiftState]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const stop = selected
    ? (run?.stops || []).find((s) => s.id === selected) || null
    : null;

  const withPosition = getPosition;

  /** Send one photo, or hold it on the device if that fails. */
  async function sendOne(blob, filename, pos) {
    const form = new FormData();
    form.append("file", blob, filename);
    form.append("kind", "proof");
    if (pos.lat != null) { form.append("lat", pos.lat); form.append("lon", pos.lon); }
    try {
      await API.post(`/driver/stops/${encodeURIComponent(stop.id)}/photos`, form);
      return "uploaded";
    } catch (err) {
      // Never lose the driver's evidence: hold it and retry later.
      await enqueue({
        orderId: stop.id, blob, filename, kind: "proof", lat: pos.lat, lon: pos.lon,
      });
      return "queued";
    }
  }

  function report(uploaded, queued) {
    setToast(queued
      ? `${queued} photo${queued > 1 ? "s" : ""} saved on the phone - will send when back online`
      : `${uploaded} photo${uploaded > 1 ? "s" : ""} uploaded`);
    load();
  }

  /** Installed app: use the real camera. Web/PWA: fall back to the file input. */
  async function capturePhoto() {
    if (!stop) return;
    if (!isNative()) { fileRef.current?.click(); return; }
    setBusy(true);
    try {
      const blob = await takePhoto();
      if (!blob) return;                       // driver cancelled
      const pos = await withPosition();
      const result = await sendOne(blob, `pod-${Date.now()}.jpg`, pos);
      await refreshQueue();
      report(result === "uploaded" ? 1 : 0, result === "queued" ? 1 : 0);
    } catch (err) {
      setToast("Could not open the camera - check the app's camera permission");
    } finally {
      setBusy(false);
    }
  }

  async function onPhotoPicked(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !stop) return;
    setBusy(true);
    const pos = await withPosition();
    let uploaded = 0;
    let queued = 0;
    for (const file of files) {
      const blob = await compressImage(file);
      const result = await sendOne(blob, file.name || "photo.jpg", pos);
      if (result === "uploaded") uploaded += 1; else queued += 1;
    }
    await refreshQueue();
    setBusy(false);
    report(uploaded, queued);
  }

  async function completeStop() {
    if (!stop) return;
    if (!window.confirm(`Mark ${stop.customer_name || stop.id} as delivered?`)) return;
    setBusy(true);
    const pos = await withPosition();
    try {
      await API.post(`/driver/stops/${encodeURIComponent(stop.id)}/complete`, pos);
      setToast("Stop completed");
      setSelected(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400 && detail?.includes("photo")) {
        const reason = window.prompt(
          "No photo for this stop. Type a reason to complete without one:", "");
        if (reason) {
          try {
            await API.post(`/driver/stops/${encodeURIComponent(stop.id)}/complete`,
              { ...pos, no_photo_reason: reason });
            setToast("Stop completed without a photo");
            setSelected(null);
            load();
          } catch (e2) {
            setToast(e2?.response?.data?.detail || "Could not complete the stop");
          }
        }
      } else {
        setToast(detail || "Could not complete - it will stay on your list");
      }
    } finally {
      setBusy(false);
    }
  }

  async function markPickedUp() {
    if (!stop) return;
    setBusy(true);
    try {
      await API.post(`/driver/stops/${encodeURIComponent(stop.id)}/picked-up`);
      setToast("Marked as picked up");
      load();
    } catch (err) {
      setToast(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  }

  /* One path for every punch, because the three outcomes have to read
   * differently: sent, held on the phone, or refused by the office. "Saved"
   * with no qualifier is what makes a driver assume the office has it. */
  async function punch(action, queuedMessage, sentMessage) {
    setClockBusy(true);
    try {
      const res = await action();
      if (res.state) setShiftState(res.state);
      await refreshClockQueue();
      setNow(Date.now());
      if (res.status === "rejected") {
        setToast(res.message);
        /* The office said no because its record differs from the phone's -
         * it may have closed the break, or the day, from the Timesheets tab.
         * Re-sync so the bar shows what the office holds, not what the
         * phone assumed. getCurrent never throws; offline it keeps the cache. */
        await loadShift();
      } else {
        setToast(res.status === "queued" ? queuedMessage : sentMessage);
      }
    } finally {
      setClockBusy(false);
    }
  }

  async function startShift() {
    /* A driver checks the truck first - the shift starts when the sheet is
     * submitted (finishPreCheck). An offsider is not driving and punches
     * straight in. */
    if (role !== "offsider") {
      setPrecheck({ mode: "start", truckId: lastTruckId() });
      return;
    }
    await punch(
      async () => clockIn(API, await withPosition()),
      "Start time saved on your phone - it will send when you have signal",
      "Clocked in"
    );
  }

  /* The Pre-check button: today's sheet for the truck on this shift (or the
   * last one checked), read-only if done, a form otherwise. */
  function openPreCheck() {
    const truckId = shiftState?.open_shift?.truck_id || shiftState?.check?.truck_id || lastTruckId();
    setPrecheck({ mode: "check", truckId });
  }

  /* The sheet was submitted, or today's existing one accepted. The check goes
   * into the queue FIRST, then the clock-in carrying its id - the queue is
   * ordered, so the office always gets the check before the punch. A check
   * the office refuses does not stop the shift: nothing is blocked on a
   * fault or a form (operator decision), the refusal sits in the red card. */
  async function finishPreCheck(result) {
    const mode = precheck?.mode || "check";
    const { payload, truck, faults, existing } = result;
    setPrecheck(null);
    setClockBusy(true);
    let checkUuid = existing?.client_uuid || null;
    let refused = "";
    try {
      if (payload) {
        const res = await submitCheck(API, payload, { truck_plate: truck?.license_plate, faults });
        if (res.state) setShiftState(res.state);
        if (res.status === "rejected") {
          refused = res.message || "the office refused the pre-check";
          checkUuid = null;
        } else {
          checkUuid = res.client_uuid;
        }
        if (mode !== "start") {
          setToast(refused
            ? `The office refused the pre-check - ${refused}. Ring it in.`
            : res.status === "queued"
              ? "Pre-check saved on your phone - it will send when you have signal"
              : "Pre-check sent to the office");
        }
      } else if (existing) {
        // done earlier today (before Start shift, or before a lunch clock-out):
        // no second form, but the fault card comes back with it
        const state = rememberCheck(existing);
        if (state) setShiftState(state);
      }

      if (mode === "start") {
        const pos = result.pos?.lat != null ? result.pos : await withPosition();
        const res = await clockIn(API, {
          ...pos,
          truck_id: truck?.id,
          truck_plate: truck?.license_plate,
          check_client_uuid: checkUuid,
        });
        if (res.state) setShiftState(res.state);
        setNow(Date.now());
        if (res.status === "rejected") setToast(res.message);
        else if (refused) setToast(`Clocked in - but the office refused the pre-check (${refused}). Ring it in.`);
        else if (res.status === "queued") setToast("Pre-check and start time saved on your phone - they will send when you have signal");
        else setToast(payload ? "Pre-check sent · clocked in" : "Clocked in");
      }
    } finally {
      setClockBusy(false);
      await refreshClockQueue();
    }
  }

  async function finishShift() {
    const worked = elapsedLabel(shiftState?.open_shift?.started_at, Date.now());
    if (!window.confirm(`Finish your shift?${worked ? ` ${worked} on the clock.` : ""}`)) return;
    await punch(
      () => clockOut(API),
      "Finish time saved on your phone - it will send when you have signal",
      "Clocked out"
    );
  }

  /* A break is two punches - when it started and when it ended - not a
   * number of minutes typed after the fact. Still paid: the hours stay
   * finish minus start, the punches only record when the break was. */
  async function beginBreak() {
    await punch(
      () => startBreak(API),
      "Break start saved on your phone - it will send when you have signal",
      "Break started"
    );
  }

  async function stopBreak() {
    await punch(
      () => endBreak(API),
      "Break end saved on your phone - it will send when you have signal",
      "Break ended"
    );
  }

  async function clearFailed(id) {
    await dismissFailed(id);
    await refreshClockQueue();
  }

  const stops = run?.stops || [];
  const done = stops.filter((s) => s.delivered).length;
  const openShift = shiftState?.open_shift || null;
  // The break still running, and the finished ones for the shift line. The
  // server's on_break wins when it is there; a cached shift from before this
  // change has neither key and simply reads as "not on a break".
  const openBreak = openBreakOf(openShift);
  const onBreak = !!openShift && (openShift.on_break != null ? !!openShift.on_break : !!openBreak);
  const finishedBreaks = (openShift?.breaks || []).filter((b) => !isOpenBreak(b));
  // The plate on the bar: the server's, else the cached fleet list by id (an
  // older server sends the id alone), else the truck the day's check was on.
  const shiftPlate = openShift
    ? (openShift.truck_plate || plateFor(openShift.truck_id) || shiftState?.check?.truck_plate || "")
    : "";
  // The amber card stays up for the whole shift, or all day for a check done
  // before Start shift - the cache is pruned on sync but not at midnight.
  const dayCheck = shiftState?.check && (openShift || shiftState.check.check_date === deviceDate())
    ? shiftState.check : null;
  const faultCheck = dayCheck?.status === "fault" ? dayCheck : null;
  // Named on the sign-out button so whoever picks up a shared cab phone can
  // see at a glance whose account is still on it.
  const signedInAs = localStorage.getItem("dav_username") || "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-24">
      {/* header */}
      <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="px-4 py-3 flex items-center gap-3">
          {stop ? (
            <button onClick={() => setSelected(null)} className="p-2 -ml-2 rounded-lg hover:bg-slate-800" aria-label="Back">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-sm">DAV</div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-bold leading-tight truncate">
              {stop ? (stop.customer_name || `Stop ${stop.id}`) : "Today's run"}
            </h1>
            <p className="text-xs text-slate-400">
              {stop ? stop.invoice : `${done} of ${stops.length} delivered${run?.day ? ` · ${run.day}` : ""}`}
            </p>
          </div>
          {!online && (
            <span className="flex items-center gap-1 text-[11px] bg-amber-500/15 text-amber-400 px-2 py-1 rounded-full">
              <CloudOff size={12} /> Offline
            </span>
          )}
          {pending > 0 && (
            <span className="flex items-center gap-1 text-[11px] bg-blue-500/15 text-blue-300 px-2 py-1 rounded-full">
              <Upload size={12} /> {pending}
            </span>
          )}
          {!stop && (
            <button onClick={() => { setLoading(true); load(); }} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Refresh">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
        {stops.length > 0 && !stop && (
          <div className="h-1 bg-slate-800">
            <div className="h-full bg-emerald-500 transition-all"
                 style={{ width: `${Math.round((done / stops.length) * 100)}%` }} />
          </div>
        )}
      </div>

      {toast && (
        <div className="mx-4 mt-3 bg-slate-800 border border-slate-700 text-sm px-3 py-2 rounded-xl">
          {toast}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading your run…
        </div>
      ) : error ? (
        <div className="m-4 bg-red-500/10 border border-red-500/30 text-red-300 p-4 rounded-2xl text-sm">
          <AlertTriangle size={16} className="inline mr-1" /> {error}
          <button onClick={load} className="block mt-3 bg-red-500/20 px-3 py-1.5 rounded-lg">Try again</button>
        </div>
      ) : stop ? (
        /* ---------------- stop detail ---------------- */
        <div className="p-4 space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3">
            <div className="flex items-start gap-2">
              <MapPin size={18} className="text-blue-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium leading-snug">{stop.address || "No address on this stop"}</p>
                {stop.pickup_from && (
                  <p className="text-xs text-slate-400 mt-1">Collect from {stop.pickup_from}</p>
                )}
              </div>
            </div>
            {stop.address && (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}`}
                 target="_blank" rel="noreferrer"
                 className="block text-center bg-slate-800 hover:bg-slate-700 py-2.5 rounded-xl text-sm font-medium">
                Navigate
              </a>
            )}
            {stop.phone && (
              <a href={`tel:${stop.phone}`}
                 className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 py-2.5 rounded-xl text-sm font-medium">
                <Phone size={15} /> {stop.phone}
              </a>
            )}
          </div>

          {stop.instructions && (
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-4">
              <p className="text-[11px] uppercase tracking-wider text-amber-400 font-bold mb-1">Instructions</p>
              <p className="text-sm text-amber-100">{stop.instructions}</p>
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1">
              <Package size={13} /> Items ({stop.products?.length || 0})
            </p>
            {stop.products?.length ? (
              <ul className="space-y-1.5">
                {stop.products.map((p, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-slate-500 shrink-0">{p.quantity || 1}×</span>
                    <span>{p.description || p.sku || "Item"}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No items recorded on the paperwork.</p>
            )}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-3">
              Proof of delivery ({stop.photo_count || 0} photo{stop.photo_count === 1 ? "" : "s"})
            </p>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                   multiple onChange={onPhotoPicked} className="hidden" />
            <button onClick={capturePhoto} disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 py-3.5 rounded-xl font-semibold">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              Take photo
            </button>
          </div>

          {!stop.delivered ? (
            <div className="space-y-2">
              {!stop.picked_up && (
                <button onClick={markPickedUp} disabled={busy}
                        className="w-full bg-slate-800 hover:bg-slate-700 py-3 rounded-xl font-medium disabled:opacity-50">
                  <Truck size={16} className="inline mr-2" /> Mark picked up
                </button>
              )}
              <button onClick={completeStop} disabled={busy}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 py-4 rounded-xl font-bold text-lg disabled:opacity-50">
                <CheckCircle2 size={20} className="inline mr-2" /> Delivered
              </button>
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-2xl p-4 text-center text-sm font-medium">
              <CheckCircle2 size={18} className="inline mr-2" />
              {/* delivered_at arrives without an offset, and the ES spec parses an
                  offsetless date-time as LOCAL time - so new Date() read a 19:43 NZ
                  delivery back as 07:43 and drivers reported deliveries "timed
                  wrong". nzClock() treats it as the UTC it actually is and formats
                  it in Pacific/Auckland. */}
              Delivered{stop.delivered_at ? ` · ${nzClock(stop.delivered_at)}` : ""}
            </div>
          )}
        </div>
      ) : (
        /* ---------------- stop list ---------------- */
        <div className="p-4 space-y-3">
          {run?.message && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-center text-sm text-slate-400">
              {run.message}
            </div>
          )}
          {stops.map((s, i) => (
            <button key={s.id} onClick={() => setSelected(s.id)}
                    className={`w-full text-left bg-slate-900 border rounded-2xl p-4 active:scale-[0.99] transition ${
                      s.delivered ? "border-emerald-500/30 opacity-60" : "border-slate-800"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${
                  s.delivered ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-300"}`}>
                  {s.delivered ? <CheckCircle2 size={16} /> : (s.sequence ?? i + 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{s.customer_name || `Stop ${s.id}`}</p>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{s.address || "No address"}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                    {s.products?.length > 0 && <span>{s.products.length} item{s.products.length > 1 ? "s" : ""}</span>}
                    {s.photo_count > 0 && <span className="text-blue-400">{s.photo_count} photo{s.photo_count > 1 ? "s" : ""}</span>}
                    {s.instructions && <span className="text-amber-500">note</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
          {onExit && (
            <button onClick={onExit} className="w-full text-slate-500 text-sm py-4">
              Back to dashboard
            </button>
          )}
        </div>
      )}

      {/* ---------------- my hours ----------------
          A driver's own start and finish times, one row per shift, for this
          fortnight or the last. Read-only here: a wrong day is corrected on
          the office's Timesheets tab, with an audit row, not from the cab. */}
      {hoursOpen && (() => {
        const rows = (hoursData?.shifts || []).slice().sort((a, b) =>
          String(b.work_date).localeCompare(String(a.work_date)) || String(b.start || "").localeCompare(String(a.start || "")));
        const paid = rows.reduce((t, s) => t + (Number(s.paid_hours) || 0), 0);
        const breaks = rows.reduce((t, s) => t + (Number(s.break_minutes) || 0), 0);
        const days = new Set(rows.map((s) => s.work_date)).size;
        const stale = hoursData && hoursData.offset !== hoursOffset;
        return (
          <div className="fixed inset-0 z-40 bg-slate-950 text-slate-100 flex flex-col">
            <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-800">
              <div className="flex-1 min-w-0">
                <h2 className="font-bold leading-tight">My hours</h2>
                <p className="text-xs text-slate-400 truncate">
                  {hoursData?.from && !stale
                    ? `${prettyDay(hoursData.from)} – ${prettyDay(hoursData.to)}${hoursData.status === "approved" ? " · approved" : ""}`
                    : hoursOffset === 0 ? "This fortnight" : "Last fortnight"}
                </p>
              </div>
              <button onClick={() => loadHours(hoursOffset)} disabled={hoursBusy} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Refresh">
                <RefreshCw size={17} className={hoursBusy ? "animate-spin" : ""} />
              </button>
              <button onClick={() => setHoursOpen(false)} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="px-4 pt-3 flex gap-2">
              {[["This fortnight", 0], ["Last fortnight", -1]].map(([label, off]) => (
                <button key={off} onClick={() => { setHoursOffset(off); loadHours(off); }}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold ${hoursOffset === off ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="px-4 pt-3 grid grid-cols-3 gap-2">
              {[
                // against the personal cap when the office set one, else the
                // company's 140-hour fortnight
                ["Paid hours", hoursOffset === 0 && (shiftState?.fortnight?.cap_hours ?? shiftState?.fortnight?.limit_hours) != null
                  ? `${paid.toFixed(2)} / ${Number(shiftState.fortnight.cap_hours ?? shiftState.fortnight.limit_hours)}`
                  : paid.toFixed(2)],
                ["Days", days],
                ["Breaks", breaks ? `${breaks} min` : "—"],
              ].map(([l, v]) => (
                <div key={l} className="bg-slate-900 border border-slate-800 rounded-xl px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
                  <div className="text-lg font-bold">{v}</div>
                </div>
              ))}
            </div>

            {hoursErr && (
              <p className="mx-4 mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                {hoursErr}{hoursData ? " - showing the last copy this phone saw." : ""}
              </p>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {hoursBusy && !hoursData && <Loader2 className="animate-spin mx-auto mt-8 text-slate-500" />}
              {!hoursBusy && rows.length === 0 && !hoursErr && (
                <p className="text-center text-sm text-slate-500 mt-8">No hours recorded for this fortnight yet.</p>
              )}
              {rows.map((s) => (
                <div key={s.id} className={`bg-slate-900 border rounded-xl px-3 py-2.5 ${s.flags?.some((f) => f.tier === "over_limit") ? "border-red-500/40" : s.flags?.some((f) => f.tier === "check") ? "border-amber-500/40" : "border-slate-800"}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-[4.6rem] shrink-0 text-xs font-semibold text-slate-300">{prettyDay(s.work_date)}</div>
                    <div className="flex-1 text-sm font-semibold tabular-nums">
                      {s.start || "—"} <span className="text-slate-500 font-normal">→</span>{" "}
                      {s.finish || <span className="text-emerald-300">on shift</span>}
                    </div>
                    <div className="text-sm font-bold tabular-nums">{s.paid_hours != null ? `${Number(s.paid_hours).toFixed(2)} h` : "—"}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                    {/* Each break with its times, as the office formatted them.
                        A day that only has a minutes total (Log Ninja, keyed in
                        before breaks were timed) still shows that total. */}
                    {(s.breaks || []).length > 0
                      ? s.breaks.map((b) => (
                        <span key={b.id || b.client_uuid || breakStart(b)}>
                          {isOpenBreak(b)
                            ? `break since ${breakStart(b)} (paid)`
                            : `break ${breakSpan(b)} · ${Number(b.minutes) || 0} min (paid)`}
                        </span>
                      ))
                      : s.break_minutes > 0 && <span>break {s.break_minutes} min (paid)</span>}
                    {s.source === "manual" && <span className="text-violet-300">keyed in by the office</span>}
                    {s.source === "logninja" && <span>from Log Ninja</span>}
                    {s.locked && <span>approved</span>}
                    {(s.flags || []).map((f, i) => (
                      <span key={i} title={f.message} className={f.tier === "over_limit" ? "text-red-300" : f.tier === "check" ? "text-amber-300" : ""}>{f.code}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="px-4 py-3 text-center text-[10px] text-slate-600 border-t border-slate-800">
              Pay record only · not a logbook. Something wrong? Tell the office - a day is corrected on Timesheets, with a record of who changed it.
            </p>
          </div>
        );
      })()}

      {/* ---------------- pre-check ----------------
          Full-screen, above everything including the hours sheet. Closing it
          from "start" mode punches nothing: the driver is simply not on shift. */}
      {precheck && (
        <PreCheck mode={precheck.mode} truckId={precheck.truckId}
                  onDone={finishPreCheck} onClose={() => setPrecheck(null)} />
      )}

      {/* ---------------- shift bar ----------------
          Sits outside the loading/error/stop ternary on purpose. sw.js caches no
          API response, so a cold open with no signal turns the whole screen into
          the error card - and a driver whose run will not load, or who has no run
          assigned at all, still has to be able to start and finish their day. */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-slate-900/95 backdrop-blur border-t border-slate-800 px-4 pt-2.5 pb-3">
        {clockFailed.length > 0 && (
          <div className="mb-2 bg-red-500/10 border border-red-500/30 rounded-xl p-2.5 space-y-1">
            {/* A punch (or pre-check) the office refused will never send itself.
                Silence here is what turns into an unpaid day, or a truck with
                no check on file, so it stays on screen until the driver has
                seen it. */}
            <p className="text-[11px] font-bold text-red-300 flex items-center gap-1">
              <AlertTriangle size={12} />
              The office could not record {clockFailed.length} {clockFailed.length > 1 ? "entries" : "entry"} - ring it in
            </p>
            {clockFailed.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-[11px] text-red-200">
                <span className="flex-1 min-w-0 truncate">
                  {punchLabel(e)} {nzClock(e.occurred_at)} · {e.reason}
                </span>
                <button onClick={() => clearFailed(e.id)}
                        className="shrink-0 px-2 py-0.5 rounded-md bg-red-500/20 hover:bg-red-500/30">
                  Got it
                </button>
              </div>
            ))}
          </div>
        )}

        {faultCheck && (
          <div className="mb-2 bg-amber-500/10 border border-amber-500/40 rounded-xl p-2.5">
            {/* Recorded and with the office (or on its way) - the driver is
                warned, not stopped. The office clears it under the truck. */}
            <p className="text-[11px] font-bold text-amber-300 flex items-center gap-1">
              <AlertTriangle size={12} className="shrink-0" />
              Fault reported on {faultCheck.truck_plate || plateFor(faultCheck.truck_id) || "the truck"} - tell the office before driving
            </p>
            {(faultCheck.faults || []).length > 0 && (
              <div className="mt-0.5 text-[11px] text-amber-200/80">
                {/* "Mirrors - cracked left": the driver's own words ride with
                    the item so the office hears the same thing on the phone
                    as it reads on the sheet. */}
                {faultCheck.faults.map((f) => {
                  const note = f.note || faultCheck.item_notes?.[f.key];
                  return <p key={f.key}>{f.label || f.key}{note ? ` - ${note}` : ""}</p>;
                })}
              </div>
            )}
          </div>
        )}

        {openShift ? (
          <>
            <div className="flex items-center gap-2">
              {/* The NZ "HH:MM" the API already formatted - the frontend does no
                  timezone maths. Breaks are paid: shown as recorded (start to
                  finish), never as a deduction, because the hours are finish
                  minus start. A shift keyed in with only a minutes total (Log
                  Ninja, the old picker) still shows that total. */}
              <p className="min-w-0 flex-1 text-sm font-semibold truncate">
                {elapsedLabel(openShift.started_at, now)}
                <span className="font-normal text-slate-400 text-[11px]">
                  {" "}on shift since {openShift.start || nzClock(openShift.started_at)}
                  {shiftPlate ? ` · ${shiftPlate}` : ""}
                  {finishedBreaks.length > 0
                    ? ` · break ${finishedBreaks.map(breakSpan).join(", ")} (paid)`
                    : openShift.break_minutes > 0 ? ` · break ${openShift.break_minutes} min (paid)` : ""}
                </span>
              </p>
              {clockPending > 0 && (
                <span className="shrink-0 flex items-center gap-1 text-[11px] bg-blue-500/15 text-blue-300 px-2 py-1 rounded-full">
                  <CloudOff size={11} /> {clockPending} to send
                </span>
              )}
            </div>
            {onBreak && (
              <p className="mt-1 text-[11px] text-amber-300 flex items-center gap-1">
                {/* Since-time is the office's NZ string, or nzClock of the punch
                    still on the phone; the minutes tick with the 20s timer. */}
                <Coffee size={11} className="shrink-0" />
                on break since {openBreak ? breakStart(openBreak) : "now"}
                {openBreak ? ` · ${breakElapsed(openBreak.started_at, now)}` : ""}
                {openBreak?.pending ? " · held on this phone" : ""}
              </p>
            )}
            <div className="flex gap-2 mt-2">
              {onBreak ? (
                <button onClick={stopBreak} disabled={clockBusy}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 py-2.5 rounded-xl text-sm font-semibold">
                  <Coffee size={15} /> End break
                </button>
              ) : (
                <button onClick={beginBreak} disabled={clockBusy}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 py-2.5 rounded-xl text-sm font-semibold">
                  <Coffee size={15} /> Start break
                </button>
              )}
              <button onClick={finishShift} disabled={clockBusy}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 py-2.5 rounded-xl text-sm font-bold">
                {clockBusy ? <Loader2 size={15} className="animate-spin" /> : <LogOut size={15} />} Finish shift
              </button>
            </div>
          </>
        ) : (
          <>
            <button onClick={startShift} disabled={clockBusy}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 py-3.5 rounded-xl font-bold">
              {clockBusy ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
              Start shift
            </button>
            {clockPending > 0 && (
              <p className="mt-1.5 flex items-center justify-center gap-1 text-center text-[11px] text-blue-300">
                {/* No Background Sync anywhere in this app - be honest about when
                    a held punch actually leaves the phone. */}
                <CloudOff size={11} className="shrink-0" />
                {clockPending} {clockPending > 1 ? "entries" : "entry"} held on this phone - sends when you next open the app on signal
              </p>
            )}
          </>
        )}

        <div className="flex gap-2 mt-2">
          <button onClick={() => openHours(0)}
                  className="flex-1 min-w-0 flex items-center justify-center gap-1.5 text-[12px] font-semibold text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-800 py-2 px-2 rounded-lg">
            <Clock size={13} className="shrink-0" />
            <span className="truncate">
              My hours
              {shiftState?.fortnight?.paid_hours != null && (
                <span className="text-slate-500 font-normal">
                  · {Number(shiftState.fortnight.paid_hours).toFixed(1)} h this fortnight
                  {(shiftState.fortnight.cap_hours ?? shiftState.fortnight.limit_hours) != null && (() => {
                    const personal = shiftState.fortnight.cap_hours != null;
                    const limit = Number(personal ? shiftState.fortnight.cap_hours : shiftState.fortnight.limit_hours);
                    const left = Number(personal ? shiftState.fortnight.remaining : shiftState.fortnight.limit_remaining);
                    return (
                      <span className={left === 0 ? " text-amber-300" : ""}>
                        {" "}({left === 0 ? "over the" : `${left.toFixed(1)} h left of the`} {limit} h {personal ? "cap" : "fortnight limit"})
                      </span>
                    );
                  })()}

                </span>
              )}
            </span>
          </button>
          {/* Never shown to an offsider: they have no truck to be asked about. */}
          {role !== "offsider" && (
            <button onClick={openPreCheck} disabled={clockBusy}
                    className={`shrink-0 flex items-center justify-center gap-1.5 text-[12px] font-semibold py-2 px-3 rounded-lg disabled:opacity-50 ${
                      faultCheck ? "text-amber-200 bg-amber-500/15 hover:bg-amber-500/25"
                        : dayCheck ? "text-emerald-200 bg-emerald-500/15 hover:bg-emerald-500/25"
                          : openShift ? "text-amber-200 bg-amber-500/15 hover:bg-amber-500/25 ring-1 ring-amber-500/40"
                            : "text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-800"}`}>
              {/* On shift with no sheet yet: amber until it is done - a driver
                  who started first must not forget the truck was never checked. */}
              <ClipboardCheck size={13} /> Pre-check{dayCheck ? (dayCheck.pending ? " · held" : " · done") : openShift ? " · not done" : ""}
            </button>
          )}
        </div>

        {/* The legal line has to live in the product, not just in a code comment:
            this is a pay record, the driver's logbook is a separate thing. */}
        <p className="mt-1.5 text-center text-[10px] text-slate-600 leading-tight">
          Pay record only · not a logbook
        </p>

        {/* Sign out lives here rather than in the stop list: a shared cab phone
            has to be handed to the next driver even when the run will not load,
            and the list is inside the error/loading branch. */}
        {onLogout && (
          <button
            onClick={() => {
              if (openShift) {
                if (!window.confirm("You are still on shift. Sign out anyway?\n\nYour shift stays open - finish it on whichever phone you sign in on next.")) return;
              }
              onLogout();
            }}
            className="w-full mt-1 text-center text-[11px] text-slate-500 hover:text-slate-300 py-1.5 transition-colors"
          >
            Sign out{signedInAs ? ` (${signedInAs})` : ""}
          </button>
        )}
      </div>
    </div>
  );
}
